import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";

const api = new API();

/*
|--------------------------------------------------------------------------
| DEFAULT FILTERS
|--------------------------------------------------------------------------
*/

const initialFilters = {
  search: "",
  importType: "",
  sourceSoftware: "",
  status: "",

  fromDate: "",
  toDate: "",

  sortBy: "createdAt",
  sortOrder: "desc",

  page: 1,
  limit: 20,
};

/*
|--------------------------------------------------------------------------
| DEFAULT PAGINATION
|--------------------------------------------------------------------------
*/

const initialPagination = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/*
|--------------------------------------------------------------------------
| DEFAULT IMPORT SUMMARY
|--------------------------------------------------------------------------
*/

const initialImportSummary = {
  totalRows: 0,
  validRows: 0,
  invalidRows: 0,
  duplicateRows: 0,
  skippedRows: 0,
  importedRows: 0,
  failedRows: 0,
};

/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/

const initialState = {
  imports: [],
  selectedImport: null,

  dropdowns: {
    importTypes: [],
    sourceSoftwareOptions: [],
    statuses: [],
    duplicateHandlingOptions: [],
    dateFormats: [],
    documentTypes: [],
  },

  mapping: {
    sourceColumns: [],
    targetFields: [],
    fieldMapping: {},
    requiredFields: [],
    unmappedRequiredFields: [],
  },

  preview: {
    columns: [],
    rows: [],
    errors: [],
    warnings: [],

    summary: {
      ...initialImportSummary,
    },
  },

  importRows: [],

  rowFilters: {
    validationStatus: "",
    importStatus: "",
    search: "",
    page: 1,
    limit: 50,
  },

  rowPagination: {
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  },

  filters: {
    ...initialFilters,
  },

  pagination: {
    ...initialPagination,
  },

  listLoading: false,
  detailsLoading: false,
  dropdownsLoading: false,
  uploadLoading: false,
  mappingLoading: false,
  previewLoading: false,
  executeLoading: false,
  rowsLoading: false,
  cancelLoading: false,
  deleteLoading: false,

  error: null,
  actionError: null,

  operationSuccess: false,
  operationMessage: "",

  lastUploadedImport: null,
  lastExecutedImport: null,
};

/*
|--------------------------------------------------------------------------
| RESPONSE HELPERS
|--------------------------------------------------------------------------
*/

const getResponseData = (
  response,
  fallback = {}
) => {
  if (!response) {
    return fallback;
  }

  return (
    response.data ||
    response.result ||
    response
  );
};

const getErrorMessage = (
  response,
  fallbackMessage
) => {
  return (
    response?.message ||
    response?.error?.message ||
    response?.error ||
    fallbackMessage
  );
};

const rejectIfFailed = (
  response,
  rejectWithValue,
  fallbackMessage
) => {
  if (
    !response ||
    response.success === false ||
    response.status === "error"
  ) {
    return rejectWithValue(
      getErrorMessage(
        response,
        fallbackMessage
      )
    );
  }

  return null;
};

const extractImport = (
  payload
) => {
  return (
    payload?.billingImport ||
    payload?.importJob ||
    payload?.import ||
    payload
  );
};

const updateImportInList = (
  state,
  billingImport
) => {
  if (!billingImport?._id) {
    return;
  }

  const index =
    state.imports.findIndex(
      (item) =>
        item._id ===
        billingImport._id
    );

  if (index >= 0) {
    state.imports[index] =
      billingImport;
  }
};

const addImportToList = (
  state,
  billingImport
) => {
  if (!billingImport?._id) {
    return;
  }

  const exists =
    state.imports.some(
      (item) =>
        item._id ===
        billingImport._id
    );

  if (!exists) {
    state.imports.unshift(
      billingImport
    );
  }
};

/*
|--------------------------------------------------------------------------
| FETCH IMPORT LIST
|--------------------------------------------------------------------------
*/

export const fetchBillingImports =
  createAsyncThunk(
    "billingImport/fetchBillingImports",
    async (
      params = {},
      {
        getState,
        rejectWithValue,
      }
    ) => {
      const filters =
        getState()
          ?.billingImport
          ?.filters ||
        initialFilters;

      const response =
        await api.get(
          config.getBillingImports,
          {
            ...filters,
            ...params,
          }
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch billing imports."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| FETCH IMPORT DROPDOWNS
|--------------------------------------------------------------------------
*/

export const fetchBillingImportDropdowns =
  createAsyncThunk(
    "billingImport/fetchBillingImportDropdowns",
    async (
      _,
      {
        rejectWithValue,
      }
    ) => {
      const response =
        await api.get(
          config.getBillingImportDropdowns
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch billing import dropdowns."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| FETCH IMPORT BY ID
|--------------------------------------------------------------------------
*/

export const fetchBillingImportById =
  createAsyncThunk(
    "billingImport/fetchBillingImportById",
    async (
      importId,
      {
        rejectWithValue,
      }
    ) => {
      if (!importId) {
        return rejectWithValue(
          "Import ID is required."
        );
      }

      const response =
        await api.get(
          config.getBillingImportById(
            importId
          )
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch billing import details."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| UPLOAD IMPORT FILE
|--------------------------------------------------------------------------
|
| Payload:
|
| {
|   file,
|   importType,
|   sourceSoftware,
|   dateFormat,
|   duplicateHandling,
|   branchId,
|   firmId,
|   warehouseId
| }
|--------------------------------------------------------------------------
*/

export const uploadBillingImport =
  createAsyncThunk(
    "billingImport/uploadBillingImport",
    async (
      {
        file,
        importType,
        sourceSoftware = "",
        dateFormat = "",
        duplicateHandling = "",
        branchId = "",
        firmId = "",
        warehouseId = "",
        additionalFields = {},
      },
      {
        rejectWithValue,
      }
    ) => {
      if (!file) {
        return rejectWithValue(
          "Please select an Excel or CSV file."
        );
      }

      if (!importType) {
        return rejectWithValue(
          "Import type is required."
        );
      }

      const formData =
        new FormData();

      formData.append(
        "file",
        file
      );

      formData.append(
        "importType",
        importType
      );

      if (sourceSoftware) {
        formData.append(
          "sourceSoftware",
          sourceSoftware
        );
      }

      if (dateFormat) {
        formData.append(
          "dateFormat",
          dateFormat
        );
      }

      if (duplicateHandling) {
        formData.append(
          "duplicateHandling",
          duplicateHandling
        );
      }

      if (branchId) {
        formData.append(
          "branchId",
          branchId
        );
      }

      if (firmId) {
        formData.append(
          "firmId",
          firmId
        );
      }

      if (warehouseId) {
        formData.append(
          "warehouseId",
          warehouseId
        );
      }

      Object.entries(
        additionalFields || {}
      ).forEach(
        ([key, value]) => {
          if (
            value === undefined ||
            value === null ||
            value === ""
          ) {
            return;
          }

          formData.append(
            key,
            typeof value ===
              "object"
              ? JSON.stringify(
                  value
                )
              : String(value)
          );
        }
      );

      const response =
        await api.postFormData(
          config.uploadBillingImport,
          formData
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to upload billing import file."
        );

      if (failed) {
        return failed;
      }

      return {
        data:
          getResponseData(
            response,
            {}
          ),

        message:
          response?.message ||
          "Billing import file uploaded successfully.",
      };
    }
  );

/*
|--------------------------------------------------------------------------
| SAVE COLUMN MAPPING
|--------------------------------------------------------------------------
|
| Payload example:
|
| {
|   fieldMapping: {
|     "Invoice Number": "documentNumber",
|     "Invoice Date": "documentDate",
|     "Customer Name": "customerName"
|   }
| }
|--------------------------------------------------------------------------
*/

export const saveBillingImportMapping =
  createAsyncThunk(
    "billingImport/saveBillingImportMapping",
    async (
      {
        importId,
        fieldMapping,
        options = {},
      },
      {
        rejectWithValue,
      }
    ) => {
      if (!importId) {
        return rejectWithValue(
          "Import ID is required."
        );
      }

      if (
        !fieldMapping ||
        typeof fieldMapping !==
          "object"
      ) {
        return rejectWithValue(
          "Column mapping is required."
        );
      }

      const response =
        await api.put(
          config.saveBillingImportMapping(
            importId
          ),
          {
            fieldMapping,
            ...options,
          }
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to save billing import mapping."
        );

      if (failed) {
        return failed;
      }

      return {
        data:
          getResponseData(
            response,
            {}
          ),

        message:
          response?.message ||
          "Billing import mapping saved successfully.",
      };
    }
  );

/*
|--------------------------------------------------------------------------
| PREVIEW IMPORT
|--------------------------------------------------------------------------
*/

export const previewBillingImport =
  createAsyncThunk(
    "billingImport/previewBillingImport",
    async (
      {
        importId,
        payload = {},
      },
      {
        rejectWithValue,
      }
    ) => {
      if (!importId) {
        return rejectWithValue(
          "Import ID is required."
        );
      }

      const response =
        await api.post(
          config.previewBillingImport(
            importId
          ),
          payload
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to preview billing import."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| EXECUTE IMPORT
|--------------------------------------------------------------------------
*/

export const executeBillingImport =
  createAsyncThunk(
    "billingImport/executeBillingImport",
    async (
      {
        importId,
        payload = {},
      },
      {
        rejectWithValue,
      }
    ) => {
      if (!importId) {
        return rejectWithValue(
          "Import ID is required."
        );
      }

      const response =
        await api.post(
          config.executeBillingImport(
            importId
          ),
          payload
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to execute billing import."
        );

      if (failed) {
        return failed;
      }

      return {
        data:
          getResponseData(
            response,
            {}
          ),

        message:
          response?.message ||
          "Billing import completed successfully.",
      };
    }
  );

/*
|--------------------------------------------------------------------------
| FETCH IMPORT ROWS
|--------------------------------------------------------------------------
*/

export const fetchBillingImportRows =
  createAsyncThunk(
    "billingImport/fetchBillingImportRows",
    async (
      {
        importId,
        params = {},
      },
      {
        getState,
        rejectWithValue,
      }
    ) => {
      if (!importId) {
        return rejectWithValue(
          "Import ID is required."
        );
      }

      const rowFilters =
        getState()
          ?.billingImport
          ?.rowFilters ||
        initialState.rowFilters;

      const response =
        await api.get(
          config.getBillingImportRows(
            importId
          ),
          {
            ...rowFilters,
            ...params,
          }
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to fetch billing import rows."
        );

      if (failed) {
        return failed;
      }

      return getResponseData(
        response,
        {}
      );
    }
  );

/*
|--------------------------------------------------------------------------
| CANCEL IMPORT
|--------------------------------------------------------------------------
*/

export const cancelBillingImport =
  createAsyncThunk(
    "billingImport/cancelBillingImport",
    async (
      {
        importId,
        reason = "",
      },
      {
        rejectWithValue,
      }
    ) => {
      if (!importId) {
        return rejectWithValue(
          "Import ID is required."
        );
      }

      const response =
        await api.patch(
          config.cancelBillingImport(
            importId
          ),
          {
            reason:
              reason.trim(),
          }
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to cancel billing import."
        );

      if (failed) {
        return failed;
      }

      return {
        data:
          getResponseData(
            response,
            {}
          ),

        message:
          response?.message ||
          "Billing import cancelled successfully.",
      };
    }
  );

/*
|--------------------------------------------------------------------------
| DELETE IMPORT
|--------------------------------------------------------------------------
*/

export const deleteBillingImport =
  createAsyncThunk(
    "billingImport/deleteBillingImport",
    async (
      importId,
      {
        rejectWithValue,
      }
    ) => {
      if (!importId) {
        return rejectWithValue(
          "Import ID is required."
        );
      }

      const response =
        await api.delete(
          config.deleteBillingImport(
            importId
          )
        );

      const failed =
        rejectIfFailed(
          response,
          rejectWithValue,
          "Unable to delete billing import."
        );

      if (failed) {
        return failed;
      }

      return {
        importId,

        message:
          response?.message ||
          "Billing import deleted successfully.",
      };
    }
  );

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const billingImportSlice =
  createSlice({
    name:
      "billingImport",

    initialState,

    reducers: {
      setBillingImportFilters: (
        state,
        action
      ) => {
        state.filters = {
          ...state.filters,
          ...(action.payload || {}),
        };
      },

      setBillingImportFilter: (
        state,
        action
      ) => {
        const {
          key,
          value,
        } =
          action.payload || {};

        if (!key) {
          return;
        }

        state.filters[key] =
          value;

        if (
          key !== "page" &&
          key !== "limit"
        ) {
          state.filters.page = 1;
        }
      },

      setBillingImportPage: (
        state,
        action
      ) => {
        state.filters.page =
          Number(
            action.payload
          ) || 1;
      },

      setBillingImportLimit: (
        state,
        action
      ) => {
        state.filters.limit =
          Number(
            action.payload
          ) || 20;

        state.filters.page = 1;
      },

      resetBillingImportFilters: (
        state
      ) => {
        state.filters = {
          ...initialFilters,
        };
      },

      setSelectedBillingImport: (
        state,
        action
      ) => {
        state.selectedImport =
          action.payload || null;
      },

      clearSelectedBillingImport: (
        state
      ) => {
        state.selectedImport =
          null;
      },

      setBillingImportMapping: (
        state,
        action
      ) => {
        state.mapping = {
          ...state.mapping,
          ...(action.payload || {}),
        };
      },

      setBillingImportFieldMapping: (
        state,
        action
      ) => {
        const {
          sourceColumn,
          targetField,
        } =
          action.payload || {};

        if (!sourceColumn) {
          return;
        }

        state.mapping.fieldMapping[
          sourceColumn
        ] =
          targetField || "";
      },

      removeBillingImportFieldMapping: (
        state,
        action
      ) => {
        const sourceColumn =
          action.payload;

        if (!sourceColumn) {
          return;
        }

        delete state.mapping
          .fieldMapping[
            sourceColumn
          ];
      },

      clearBillingImportMapping: (
        state
      ) => {
        state.mapping = {
          sourceColumns: [],
          targetFields: [],
          fieldMapping: {},
          requiredFields: [],
          unmappedRequiredFields: [],
        };
      },

      setBillingImportRowFilters: (
        state,
        action
      ) => {
        state.rowFilters = {
          ...state.rowFilters,
          ...(action.payload || {}),
        };
      },

      setBillingImportRowFilter: (
        state,
        action
      ) => {
        const {
          key,
          value,
        } =
          action.payload || {};

        if (!key) {
          return;
        }

        state.rowFilters[key] =
          value;

        if (
          key !== "page" &&
          key !== "limit"
        ) {
          state.rowFilters.page =
            1;
        }
      },

      clearBillingImportPreview: (
        state
      ) => {
        state.preview = {
          columns: [],
          rows: [],
          errors: [],
          warnings: [],

          summary: {
            ...initialImportSummary,
          },
        };
      },

      clearBillingImportRows: (
        state
      ) => {
        state.importRows = [];

        state.rowPagination = {
          page: 1,
          limit: 50,
          total: 0,
          totalPages: 0,
          hasNextPage: false,
          hasPreviousPage: false,
        };
      },

      clearBillingImportErrors: (
        state
      ) => {
        state.error = null;
        state.actionError = null;
      },

      clearBillingImportOperation: (
        state
      ) => {
        state.operationSuccess =
          false;

        state.operationMessage =
          "";

        state.actionError =
          null;
      },

      clearLastBillingImportResult: (
        state
      ) => {
        state.lastUploadedImport =
          null;

        state.lastExecutedImport =
          null;
      },

      resetBillingImportState:
        () => ({
          ...initialState,

          filters: {
            ...initialFilters,
          },

          pagination: {
            ...initialPagination,
          },

          preview: {
            columns: [],
            rows: [],
            errors: [],
            warnings: [],

            summary: {
              ...initialImportSummary,
            },
          },
        }),
    },

    extraReducers: (
      builder
    ) => {
      /*
      |--------------------------------------------------------------------------
      | LIST
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchBillingImports.pending,
          (state) => {
            state.listLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchBillingImports.fulfilled,
          (
            state,
            action
          ) => {
            state.listLoading =
              false;

            const payload =
              action.payload || {};

            state.imports =
              payload.imports ||
              payload.billingImports ||
              payload.records ||
              payload.items ||
              [];

            state.pagination = {
              ...initialPagination,
              ...(payload.pagination ||
                {}),
            };

            if (
              payload.pagination
                ?.page
            ) {
              state.filters.page =
                payload.pagination.page;
            }

            if (
              payload.pagination
                ?.limit
            ) {
              state.filters.limit =
                payload.pagination.limit;
            }
          }
        )
        .addCase(
          fetchBillingImports.rejected,
          (
            state,
            action
          ) => {
            state.listLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch billing imports.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | DROPDOWNS
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchBillingImportDropdowns.pending,
          (state) => {
            state.dropdownsLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchBillingImportDropdowns.fulfilled,
          (
            state,
            action
          ) => {
            state.dropdownsLoading =
              false;

            const payload =
              action.payload || {};

            state.dropdowns = {
              ...state.dropdowns,
              ...(payload.dropdowns ||
                payload),
            };
          }
        )
        .addCase(
          fetchBillingImportDropdowns.rejected,
          (
            state,
            action
          ) => {
            state.dropdownsLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch billing import dropdowns.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | DETAILS
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchBillingImportById.pending,
          (state) => {
            state.detailsLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchBillingImportById.fulfilled,
          (
            state,
            action
          ) => {
            state.detailsLoading =
              false;

            const payload =
              action.payload || {};

            const billingImport =
              extractImport(
                payload
              );

            state.selectedImport =
              billingImport;

            if (
              payload.mapping ||
              billingImport?.mapping
            ) {
              state.mapping = {
                ...state.mapping,
                ...(
                  payload.mapping ||
                  billingImport.mapping
                ),
              };
            }

            if (
              payload.preview ||
              billingImport?.preview
            ) {
              const preview =
                payload.preview ||
                billingImport.preview;

              state.preview = {
                ...state.preview,
                ...preview,

                summary: {
                  ...initialImportSummary,
                  ...(preview.summary ||
                    {}),
                },
              };
            }
          }
        )
        .addCase(
          fetchBillingImportById.rejected,
          (
            state,
            action
          ) => {
            state.detailsLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch billing import details.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | UPLOAD
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          uploadBillingImport.pending,
          (state) => {
            state.uploadLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;

            state.operationMessage =
              "";
          }
        )
        .addCase(
          uploadBillingImport.fulfilled,
          (
            state,
            action
          ) => {
            state.uploadLoading =
              false;

            const payload =
              action.payload?.data ||
              {};

            const billingImport =
              extractImport(
                payload
              );

            state.lastUploadedImport =
              billingImport;

            state.selectedImport =
              billingImport;

            addImportToList(
              state,
              billingImport
            );

            if (payload.mapping) {
              state.mapping = {
                ...state.mapping,
                ...payload.mapping,
              };
            }

            state.operationSuccess =
              true;

            state.operationMessage =
              action.payload?.message ||
              "Billing import file uploaded successfully.";
          }
        )
        .addCase(
          uploadBillingImport.rejected,
          (
            state,
            action
          ) => {
            state.uploadLoading =
              false;

            state.actionError =
              action.payload ||
              action.error?.message ||
              "Unable to upload billing import file.";

            state.operationSuccess =
              false;
          }
        );

      /*
      |--------------------------------------------------------------------------
      | SAVE MAPPING
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          saveBillingImportMapping.pending,
          (state) => {
            state.mappingLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;
          }
        )
        .addCase(
          saveBillingImportMapping.fulfilled,
          (
            state,
            action
          ) => {
            state.mappingLoading =
              false;

            const payload =
              action.payload?.data ||
              {};

            const billingImport =
              extractImport(
                payload
              );

            if (billingImport?._id) {
              state.selectedImport =
                billingImport;

              updateImportInList(
                state,
                billingImport
              );
            }

            if (payload.mapping) {
              state.mapping = {
                ...state.mapping,
                ...payload.mapping,
              };
            }

            state.operationSuccess =
              true;

            state.operationMessage =
              action.payload?.message ||
              "Billing import mapping saved successfully.";
          }
        )
        .addCase(
          saveBillingImportMapping.rejected,
          (
            state,
            action
          ) => {
            state.mappingLoading =
              false;

            state.actionError =
              action.payload ||
              action.error?.message ||
              "Unable to save billing import mapping.";

            state.operationSuccess =
              false;
          }
        );

      /*
      |--------------------------------------------------------------------------
      | PREVIEW
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          previewBillingImport.pending,
          (state) => {
            state.previewLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;
          }
        )
        .addCase(
          previewBillingImport.fulfilled,
          (
            state,
            action
          ) => {
            state.previewLoading =
              false;

            const payload =
              action.payload || {};

            state.preview = {
              columns:
                payload.columns ||
                payload.previewColumns ||
                [],

              rows:
                payload.rows ||
                payload.previewRows ||
                [],

              errors:
                payload.errors ||
                [],

              warnings:
                payload.warnings ||
                [],

              summary: {
                ...initialImportSummary,
                ...(payload.summary ||
                  {}),
              },
            };

            if (payload.import) {
              state.selectedImport =
                payload.import;

              updateImportInList(
                state,
                payload.import
              );
            }

            state.operationSuccess =
              true;

            state.operationMessage =
              payload.message ||
              "Billing import preview generated successfully.";
          }
        )
        .addCase(
          previewBillingImport.rejected,
          (
            state,
            action
          ) => {
            state.previewLoading =
              false;

            state.actionError =
              action.payload ||
              action.error?.message ||
              "Unable to preview billing import.";

            state.operationSuccess =
              false;
          }
        );

      /*
      |--------------------------------------------------------------------------
      | EXECUTE
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          executeBillingImport.pending,
          (state) => {
            state.executeLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;
          }
        )
        .addCase(
          executeBillingImport.fulfilled,
          (
            state,
            action
          ) => {
            state.executeLoading =
              false;

            const payload =
              action.payload?.data ||
              {};

            const billingImport =
              extractImport(
                payload
              );

            state.lastExecutedImport =
              billingImport;

            state.selectedImport =
              billingImport;

            updateImportInList(
              state,
              billingImport
            );

            if (payload.summary) {
              state.preview.summary = {
                ...initialImportSummary,
                ...payload.summary,
              };
            }

            state.operationSuccess =
              true;

            state.operationMessage =
              action.payload?.message ||
              "Billing import completed successfully.";
          }
        )
        .addCase(
          executeBillingImport.rejected,
          (
            state,
            action
          ) => {
            state.executeLoading =
              false;

            state.actionError =
              action.payload ||
              action.error?.message ||
              "Unable to execute billing import.";

            state.operationSuccess =
              false;
          }
        );

      /*
      |--------------------------------------------------------------------------
      | ROWS
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          fetchBillingImportRows.pending,
          (state) => {
            state.rowsLoading =
              true;

            state.error = null;
          }
        )
        .addCase(
          fetchBillingImportRows.fulfilled,
          (
            state,
            action
          ) => {
            state.rowsLoading =
              false;

            const payload =
              action.payload || {};

            state.importRows =
              payload.rows ||
              payload.records ||
              payload.items ||
              [];

            state.rowPagination = {
              ...state.rowPagination,
              ...(payload.pagination ||
                {}),
            };
          }
        )
        .addCase(
          fetchBillingImportRows.rejected,
          (
            state,
            action
          ) => {
            state.rowsLoading =
              false;

            state.error =
              action.payload ||
              action.error?.message ||
              "Unable to fetch billing import rows.";
          }
        );

      /*
      |--------------------------------------------------------------------------
      | CANCEL
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          cancelBillingImport.pending,
          (state) => {
            state.cancelLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;
          }
        )
        .addCase(
          cancelBillingImport.fulfilled,
          (
            state,
            action
          ) => {
            state.cancelLoading =
              false;

            const billingImport =
              extractImport(
                action.payload?.data
              );

            state.selectedImport =
              billingImport;

            updateImportInList(
              state,
              billingImport
            );

            state.operationSuccess =
              true;

            state.operationMessage =
              action.payload?.message ||
              "Billing import cancelled successfully.";
          }
        )
        .addCase(
          cancelBillingImport.rejected,
          (
            state,
            action
          ) => {
            state.cancelLoading =
              false;

            state.actionError =
              action.payload ||
              action.error?.message ||
              "Unable to cancel billing import.";

            state.operationSuccess =
              false;
          }
        );

      /*
      |--------------------------------------------------------------------------
      | DELETE
      |--------------------------------------------------------------------------
      */

      builder
        .addCase(
          deleteBillingImport.pending,
          (state) => {
            state.deleteLoading =
              true;

            state.actionError =
              null;

            state.operationSuccess =
              false;
          }
        )
        .addCase(
          deleteBillingImport.fulfilled,
          (
            state,
            action
          ) => {
            state.deleteLoading =
              false;

            const importId =
              action.payload
                ?.importId;

            state.imports =
              state.imports.filter(
                (item) =>
                  item._id !==
                  importId
              );

            if (
              state.selectedImport
                ?._id ===
              importId
            ) {
              state.selectedImport =
                null;
            }

            state.operationSuccess =
              true;

            state.operationMessage =
              action.payload?.message ||
              "Billing import deleted successfully.";
          }
        )
        .addCase(
          deleteBillingImport.rejected,
          (
            state,
            action
          ) => {
            state.deleteLoading =
              false;

            state.actionError =
              action.payload ||
              action.error?.message ||
              "Unable to delete billing import.";

            state.operationSuccess =
              false;
          }
        );
    },
  });

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const {
  setBillingImportFilters,
  setBillingImportFilter,
  setBillingImportPage,
  setBillingImportLimit,
  resetBillingImportFilters,

  setSelectedBillingImport,
  clearSelectedBillingImport,

  setBillingImportMapping,
  setBillingImportFieldMapping,
  removeBillingImportFieldMapping,
  clearBillingImportMapping,

  setBillingImportRowFilters,
  setBillingImportRowFilter,

  clearBillingImportPreview,
  clearBillingImportRows,
  clearBillingImportErrors,
  clearBillingImportOperation,
  clearLastBillingImportResult,

  resetBillingImportState,
} = billingImportSlice.actions;

/*
|--------------------------------------------------------------------------
| SELECTORS
|--------------------------------------------------------------------------
*/

export const billingImportSelector = (
  state
) => state.billingImport;

export const billingImportListSelector = (
  state
) =>
  state.billingImport.imports;

export const selectedBillingImportSelector = (
  state
) =>
  state.billingImport
    .selectedImport;

export const billingImportDropdownsSelector = (
  state
) =>
  state.billingImport.dropdowns;

export const billingImportFiltersSelector = (
  state
) =>
  state.billingImport.filters;

export const billingImportPaginationSelector = (
  state
) =>
  state.billingImport.pagination;

export const billingImportMappingSelector = (
  state
) =>
  state.billingImport.mapping;

export const billingImportPreviewSelector = (
  state
) =>
  state.billingImport.preview;

export const billingImportRowsSelector = (
  state
) => ({
    rows:
      state.billingImport
        .importRows,

    filters:
      state.billingImport
        .rowFilters,

    pagination:
      state.billingImport
        .rowPagination,

    loading:
      state.billingImport
        .rowsLoading,
  });

export const billingImportLoadingSelector = (
  state
) => ({
    listLoading:
      state.billingImport
        .listLoading,

    detailsLoading:
      state.billingImport
        .detailsLoading,

    dropdownsLoading:
      state.billingImport
        .dropdownsLoading,

    uploadLoading:
      state.billingImport
        .uploadLoading,

    mappingLoading:
      state.billingImport
        .mappingLoading,

    previewLoading:
      state.billingImport
        .previewLoading,

    executeLoading:
      state.billingImport
        .executeLoading,

    rowsLoading:
      state.billingImport
        .rowsLoading,

    cancelLoading:
      state.billingImport
        .cancelLoading,

    deleteLoading:
      state.billingImport
        .deleteLoading,
  });

export const billingImportOperationSelector = (
  state
) => ({
    success:
      state.billingImport
        .operationSuccess,

    message:
      state.billingImport
        .operationMessage,

    error:
      state.billingImport
        .actionError,

    lastUploadedImport:
      state.billingImport
        .lastUploadedImport,

    lastExecutedImport:
      state.billingImport
        .lastExecutedImport,
  });

export default billingImportSlice.reducer;